package de.oevbs.kunde.kundenportal2.services.registration.store

import de.oevbs.kunde.kundenportal2.Pages
import de.oevbs.kunde.kundenportal2.ResetStep
import de.oevbs.kunde.kundenportal2.authentication.PortalSession
import de.oevbs.kunde.kundenportal2.components.GlobalServiceMessagesStore
import de.oevbs.kunde.kundenportal2.components.globalSubmitTracker
import de.oevbs.kunde.kundenportal2.model.services.registration.Reset
import de.oevbs.kunde.kundenportal2.navigation.visit
import de.oevbs.kunde.kundenportal2.redirectDelay
import de.oevbs.kunde.kundenportal2.services.registration.ResetUserPassClient
import de.oevbs.kunde.kundenportal2.util.tryCatchAndHandleErrors
import dev.fritz2.core.SimpleHandler
import dev.fritz2.core.invoke
import dev.fritz2.core.storeOf
import dev.fritz2.headless.validation.ComponentValidationMessage
import dev.fritz2.remote.btoa
import dev.fritz2.validation.ValidatingStore
import dev.fritz2.validation.valid
import kotlinx.browser.window
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.Flow

class ResetStore(job: Job) :
    ValidatingStore<Reset, ResetStep, ComponentValidationMessage>(
        initialData = Reset(
            username = PortalSession.current?.username ?: "",
        ),
        validation = Reset.validation,
        metadataDefault = ResetStep.USERINPUT1,
        validateAfterUpdate = false,
        job = job
    ) {

    private val client = ResetUserPassClient()

    private val progressStore = storeOf(ResetStep.USERINPUT1, job)
    val currentStep: Flow<ResetStep> = progressStore.data

    /**
     * Setzt den aktuellen ResetStep. Ändert nichts am Reset-Datenmodell.
     */
    val setStep = progressStore.update

    /**
     * Handler Schritte 1: dem User eine Mail mit Reset-Link senden
     * Die Schaltfläche „Passwort-Anfrage senden“ wurde gedrückt. Handler validiert Eingabedaten,
     * ruft dann [checkUserAndSendMail] auf und wartet auf die Serverantwort. Der Button zeigt den Ladezustand an.
     * Bei Erfolg wird eine Erfolgsseite angezeigt, andernfalls wird im aktuellen Schritt ein Fehler angezeigt.
     */
    val handleUserOrPartner: SimpleHandler<Unit> = handle { reset ->

        GlobalServiceMessagesStore.clear()

        if (!validate(reset, ResetStep.USERINPUT1).valid) {
            return@handle reset
        }

        val success = globalSubmitTracker.track {
            checkUserAndSendMail(reset.userOrPartner)
        }

        if (success) {
            progressStore.update(ResetStep.USERSUCCESS2)
            Reset()
        } else reset
    }

    /**
     * Handler Schritt 3: Neues Passwort prüfen und setzen
     * Die Schaltfläche „Neues Pass aktivieren“ wurde gedrückt. Ruft [saveNewPass] auf, nachdem die Frontend-Validierung
     * erfolgreich war, und wartet auf die Serverantwort. Der Button zeigt den Ladezustand an.
     * Bei Erfolg wird eine Erfolgsseite angezeigt, andernfalls wird im aktuellen Schritt ein Fehler angezeigt.
     */
    val handleNewPass: SimpleHandler<Unit> = handle { reset ->

        GlobalServiceMessagesStore.clear()

        if (!validate(reset, ResetStep.PASSWORD3).valid) {
            return@handle reset
        }

        val success = globalSubmitTracker.track {
            saveNewPass(reset)
        }

        if (success) {
            progressStore.update(ResetStep.PASSSUCCESS4)
            window.setTimeout({ Pages.root.visit() }, redirectDelay)
            Reset()
        } else reset
    }

    /**
     * Wird vom Schritt 1 Handler gerufen. Sendet einen Request zum Senden einer Pass-Reset-Mail an
     * den übergebenen User oder Partner. Der Service prüft, ob dieser gültig ist.
     *
     * @return true wenn User/Partner gefunden wurde und Mail verschickt wurde. Sonst false.
     */
    private suspend fun checkUserAndSendMail(userOrPartner: String): Boolean {

        tryCatchAndHandleErrors("Schritt 1 des Passwort-Resets") {
            client.sendPassResetMail(userOrPartner)
        } ?: return false

        return true
    }

    /**
     * Wird vom Schritt 3 Handler gerufen. Sendet einen Request an den Service, das neu ausgewählte Passwort
     * am Useraccount zu speichern.
     *
     * @return true wenn der Service das neue Pass setzen konnte. Sonst false.
     */
    private suspend fun saveNewPass(reset: Reset): Boolean {
        tryCatchAndHandleErrors("Schritt 3 des Passwort-Resets") {
            client.saveNewPass(
                reset.copy(
                    newPassword = btoa(reset.newPassword),
                    newPasswordRepeat = btoa(reset.newPasswordRepeat)
                )
            )
        } ?: return false
        return true
    }
}